import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, usePages } from '#context';
import React, { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// import TagManager from 'react-gtm-module';
import Navbar from '#components/Navbar/Navbar';
import Footer from '#components/Footer/Footer';
import Content from '#components/Content/Content';

const Home = lazy(() => import('#components/Home/Home'));
const Builder = lazy(() => import('#components/Builder/Builder'));
const Search = lazy(() => import('#components/Search/Search'));
const NotFound = lazy(() => import('#components/NotFound/NotFound'));
const AdminPanel = lazy(() => import('#components/Admin/AdminPanel/AdminPanel'));
const AdminLogin = lazy(() => import('#components/Admin/AdminLogin/AdminLogin'));
const AdminSidebar = lazy(() => import('#components/Admin/AdminSidebar/AdminSidebar'));
const AdminContent = lazy(() => import('#components/Admin/AdminContent/AdminContent'));
const AdminArticles = lazy(() => import('#components/Admin/AdminArticles/AdminArticles'));
const AdminDashboard = lazy(() => import('#components/Admin/AdminDashboard/AdminDashboard'));
const AdminNavigation = lazy(() => import('#components/Admin/AdminNavigation/AdminNavigation'));
const AdminDevelopment = lazy(() => import('#components/Admin/AdminDevelopment/AdminDevelopment'));
const AdminLandingPage = lazy(() => import('#components/Admin/AdminLandingPage/AdminLandingPage'));
const AdminStatics = lazy(() => import('#components/Admin/AdminStatics/AdminStatics'));
const AdminUserMenager = lazy(() => import('#components/Admin/AdminUserMenager/AdminUserMenager'));
const AdminDriveMenager = lazy(
    () => import('#components/Admin/AdminDriveMenager/AdminDriveMenager'),
);
const AdminSubpageMenager = lazy(
    () => import('#components/Admin/AdminSubpageMenager/AdminSubpageMenager'),
);
const ProtectedRoute = lazy(() => import('#components/ProtectedRoute/ProtectedRoute'));

const App = () => {
    const { mainPages, mainParams, adminPages, adminParams, loading } = usePages();
    const location = useLocation();

    useEffect(() => {
        let frameId;
        const scrollToTop = () => {
            frameId = requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        };

        if (!location.hash) {
            scrollToTop();
            return () => cancelAnimationFrame(frameId);
        }

        const targetId = decodeURIComponent(location.hash.slice(1));
        scrollToTop();

        let timeoutId;
        let hasScrolledToTarget = false;
        let observer;
        let resizeObserver;

        const stopObservingLayout = () => {
            window.clearTimeout(timeoutId);
            observer?.disconnect();
            resizeObserver?.disconnect();
            document.removeEventListener('load', handleResourceLoad, true);
            document.removeEventListener('error', handleResourceLoad, true);
        };

        const scrollToTarget = () => {
            if (hasScrolledToTarget) return;
            const target = document.getElementById(targetId);
            if (!target) return;

            hasScrolledToTarget = true;
            stopObservingLayout();
            frameId = requestAnimationFrame(() => {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        };

        const scheduleAnchorScroll = () => {
            window.clearTimeout(timeoutId);
            if (!hasScrolledToTarget) {
                timeoutId = window.setTimeout(scrollToTarget, 300);
            }
        };

        const handleResourceLoad = () => scheduleAnchorScroll();
        observer = new MutationObserver(scheduleAnchorScroll);
        observer.observe(document.body, { childList: true, subtree: true });

        resizeObserver = new ResizeObserver(scheduleAnchorScroll);
        resizeObserver.observe(document.documentElement);
        resizeObserver.observe(document.body);

        document.addEventListener('load', handleResourceLoad, true);
        document.addEventListener('error', handleResourceLoad, true);

        scheduleAnchorScroll();

        return () => {
            stopObservingLayout();
            if (frameId) cancelAnimationFrame(frameId);
        };
    }, [location.pathname, location.hash]);

    // useEffect(() => {
    //   TagManager.initialize({ import.meta.env.VITE_TAG_MENAGER_ARGS });
    // }, []);

    if (loading) return <></>;
    return (
        <Suspense fallback={<></>}>
            <Routes>
                <Route
                    path={`${adminPages.base}${adminParams.base}`}
                    element={
                        <AuthProvider>
                            <AdminPanel>
                                <Routes>
                                    <Route
                                        path={`${adminPages.login}${adminParams.login}`}
                                        element={<AdminLogin />}
                                    />
                                    <Route
                                        path={`${adminPages.dashboard}${adminParams.dashboard}`}
                                        element={
                                            <ProtectedRoute>
                                                <AdminDashboard>
                                                    <AdminSidebar />
                                                    <AdminContent>
                                                        <Routes>
                                                            <Route
                                                                path={`/`}
                                                                element={<AdminLandingPage />}
                                                            />
                                                            <Route
                                                                path={`/statics`}
                                                                element={<AdminStatics />}
                                                            />
                                                            <Route
                                                                path={`/subpage-menager`}
                                                                element={<AdminSubpageMenager />}
                                                            />
                                                            <Route
                                                                path={`/navigation`}
                                                                element={<AdminNavigation />}
                                                            />
                                                            <Route
                                                                path={`/article-menager`}
                                                                element={<AdminArticles />}
                                                            />
                                                            <Route
                                                                path={`/user-menager`}
                                                                element={<AdminUserMenager />}
                                                            />
                                                            <Route
                                                                path={`/drive`}
                                                                element={<AdminDriveMenager />}
                                                            />
                                                            <Route
                                                                path={`/develop`}
                                                                element={<AdminDevelopment />}
                                                            />
                                                        </Routes>
                                                    </AdminContent>
                                                </AdminDashboard>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path={`${adminPages.else}${adminParams.else}`}
                                        element={
                                            <Navigate
                                                to={`${adminPages.base}${adminPages.dashboard}`}
                                                replace
                                            />
                                        }
                                    />
                                </Routes>
                            </AdminPanel>
                        </AuthProvider>
                    }
                />
                <Route
                    path="*"
                    element={
                        <React.Fragment>
                            <Navbar />
                            <Content>
                                <Routes>
                                    <Route
                                        path={`${mainPages.dashboard}${mainParams.dashboard}`}
                                        element={<Home />}
                                    />
                                    <Route
                                        path={`${mainPages.notFound}${mainParams.notFound}`}
                                        element={<NotFound />}
                                    />
                                    <Route
                                        path={`${mainPages.search}${mainParams.search}`}
                                        element={<Search />}
                                    />
                                    <Route
                                        path={`${mainPages.subpages}${mainParams.subpages}`}
                                        element={<Builder />}
                                    />
                                    <Route
                                        path={`${mainPages.else}${mainParams.else}`}
                                        element={<Builder customRoute />}
                                    />
                                </Routes>
                            </Content>
                            <Footer />
                        </React.Fragment>
                    }
                />
            </Routes>
        </Suspense>
    );
};

export default App;
